import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { usePendingLeaveCount } from './timeAttendance/pendingLeave';
import { tn } from '../utils/plural';
import {
  Activity, Bell, ChartColumn, ChevronDown, CircleCheck, CircleX, Clock, CreditCard, FileText,
  LayoutDashboard, Lock, LogOut, MapPinned, Menu, Settings, ShieldCheck, Thermometer, TriangleAlert, User, Users, Wallet,
} from 'lucide-react';
import logoMark from '../assets/hardwall-logo.webp';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  // Below 1024 px the sidebar is a drawer (Layout.css): open or closed.
  const [navOpen, setNavOpen] = useState(false);
  
  // ============================================
  // 🔔 NOTIFICATION STATE
  // ============================================
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // ============================================
  // 🔔 FETCH NOTIFICATIONS
  // ============================================
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const response = await notificationsAPI.getAll({ limit: 10, unread_only: false });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // ============================================
  // 🔔 POLLING - Fetch every 30 seconds
  // ============================================
  useEffect(() => {
    if (user) {
      fetchNotifications(); // Initial fetch
      
      const interval = setInterval(() => {
        fetchNotifications();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [user]);

  // ============================================
  // 🔔 MARK AS READ
  // ============================================
  const handleMarkAsRead = async (notificationId, actionUrl) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Navigate if there's an action URL
      if (actionUrl) {
        setShowNotifications(false);
        navigate(actionUrl);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // ============================================
  // 🔔 MARK ALL AS READ
  // ============================================
  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // ============================================
  // 🔔 FORMAT TIME
  // ============================================
  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifTime.toLocaleDateString();
  };

  // ============================================
  // 🔔 NOTIFICATION ICON BY TYPE
  // ============================================
  const getNotificationIcon = (type) => {
    const icons = {
      'attendance_absent': TriangleAlert,
      'attendance_sick': Thermometer,
      'attendance_late': Clock,
      'contract_expiring_30': TriangleAlert,
      'contract_expiring_15': TriangleAlert,
      'contract_expiring_7': TriangleAlert,
      'contract_expired': TriangleAlert,
      'leave_requests': FileText,
      'payroll_generated': Wallet,
      'finance_expense_pending': CreditCard,
      'finance_budget_alert': TriangleAlert,
      'employee_changes': User,
      'overtime_submitted': Clock,
      'overtime_approved': CircleCheck,
      'overtime_rejected': CircleX
    };
    const Icon = icons[type] || Bell;
    return <Icon aria-hidden="true" />;
  };

  // The drawer closes when the page changes and on Escape.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // A menu entry closes the drawer even when it is the page already shown.
  const go = (path) => {
    setNavOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const hasPermission = (resource, action = 'view') => {
    if (user?.role === 'super_admin') {
      return true;
    }

    if (!user?.permissions || !Array.isArray(user.permissions)) {
      return false;
    }

    const actionColumnMap = {
      'view': 'can_view',
      'create': 'can_create',
      'update': 'can_edit',
      'delete': 'can_delete',
      'approve': 'can_approve'
    };

    const columnName = actionColumnMap[action];
    const permission = user.permissions.find(p => p.resource === resource);

    return Boolean(permission && permission[columnName] === true);
  };

  // Leave requests waiting for a decision, on the Time & Attendance entry.
  const pendingLeave = usePendingLeaveCount(hasPermission('attendance', 'view'));
  const badges = { pendingLeave };
  const badge = (key) => (key && badges[key] > 0 ? (
    <span className="nav-badge" aria-label={tn(t, badges[key], '{count} leave request waiting for a decision', '{count} leave requests waiting for a decision')}>
      {badges[key] > 99 ? '99+' : badges[key]}
    </span>
  ) : null);

  // Icons: lucide line icons (they replace the emoji). Sub-entries have none.
  const menuStructure = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      resource: 'dashboard'
    },
    {
      path: '/admin',
      icon: ShieldCheck,
      label: 'Admin Panel',
      checkRole: true,
      roles: ['super_admin']
    },
    {
      type: 'dropdown',
      icon: Users,
      label: 'Employees',
      items: [
        { path: '/employees', label: 'All Employees', resource: 'employees' },
        { path: '/documents', label: 'Documents', resource: 'documents' },
        { path: '/contracts', label: 'Contracts', resource: 'contracts' },
      ]
    },
    {
      type: 'dropdown',
      icon: MapPinned,
      label: 'Operations',
      items: [
        { path: '/clients', label: 'Clients & Sites', resource: 'clients' },
        { path: '/shifts', label: 'Shifts', resource: 'schedules' },
      ]
    },
    {
      type: 'dropdown',
      icon: Clock,
      label: 'Time & Attendance',
      items: [
        // Replaces Attendance and Time Management (their URLs redirect here).
        { path: '/time-attendance', label: 'Time & Attendance', resource: 'attendance', badge: 'pendingLeave' },
        { path: '/overtime-configuration', label: 'Overtime Config', resource: 'settings' },
        { path: '/public-holidays', label: 'Public Holidays', checkRole: true, roles: ['super_admin', 'hr_manager'] },
      ]
    },
    {
      type: 'dropdown',
      icon: Wallet,
      label: 'Payroll & Finance',
      items: [
        { path: '/payroll', label: 'Payroll', resource: 'payroll' },
        { path: '/finance', label: 'Finance', resource: 'finance' },
        { path: '/tax-configuration', label: 'Tax Config', resource: 'tax_configuration' },
        { path: '/overtime-tax-configuration', label: 'Overtime Tax', resource: 'tax_configuration' },
        { path: '/benefits', label: 'Benefits', resource: 'benefits' },
        { path: '/deductions', label: 'Deductions', resource: 'finance' },
      ]
    },
    {
      type: 'dropdown',
      icon: CircleCheck,
      label: 'Approvals',
      items: [
        { path: '/approvals', label: 'Pending Approvals', resource: 'leaves', action: 'approve' },
        { path: '/consolidations', label: 'Payroll Consolidation', resource: 'payroll', action: 'approve' },
      ]
    },
    {
      path: '/reports',
      icon: ChartColumn,
      label: 'Reports',
      resource: 'reports'
    },
  ];

  const filteredMenu = menuStructure.filter(item => {
    if (item.type === 'dropdown') {
      if (item.checkRole && item.roles) {
        return item.roles.includes(user?.role);
      }

      // Same rule as filterDropdownItems: an entry for given roles, or a permission.
      return item.items.some(subItem => (subItem.checkRole && subItem.roles
        ? subItem.roles.includes(user?.role)
        : hasPermission(subItem.resource, subItem.action || 'view')));
    }
    
    if (item.checkRole && item.roles) {
      return item.roles.includes(user?.role);
    }
    
    if (item.resource) {
      return hasPermission(item.resource, item.action || 'view');
    }
    
    if (item.roles) {
      return item.roles.includes(user?.role);
    }
    
    return false;
  });

  const filterDropdownItems = (items) => {
    return items.filter(subItem => {
      if (subItem.checkRole && subItem.roles) {
        return subItem.roles.includes(user?.role);
      }
      return hasPermission(subItem.resource, subItem.action || 'view');
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || '??';
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="layout">
      {/* Top Navbar */}
      <div className="top-navbar">
        <div className="navbar-left">
          <button
            type="button"
            className="nav-toggle"
            aria-label={t('Menu')}
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
            onClick={() => setNavOpen(open => !open)}
          >
            <Menu aria-hidden="true" />
          </button>
          <div className="company-logo">
            <img className="logo-mark" src={logoMark} alt="" width="40" height="40" />
            <div className="company-name">
              <h1>Hardwall Security</h1>
              <p>Management System</p>
            </div>
          </div>
        </div>

        <div className="navbar-right">
          <LanguageSwitcher />
          {/* ============================================ */}
          {/* 🔔 NOTIFICATION BELL - UPDATED */}
          {/* ============================================ */}
          <div className="notification-container">
            <button 
              className="icon-button" 
              title="Notifications"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
              <Bell aria-hidden="true" />
            </button>

            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllAsRead} className="mark-all-read-btn">
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="notification-list">
                  {loadingNotifications ? (
                    <div className="notification-loading">Loading...</div>
                  ) : notifications.length === 0 ? (
                    <div className="notification-empty">
                      <Bell aria-hidden="true" />
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`notification-item ${!notif.is_read ? 'unread' : ''}`}
                        onClick={() => handleMarkAsRead(notif.id, notif.action_url)}
                      >
                        <div className="notification-icon">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="notification-content">
                          <div className="notification-title">{notif.title}</div>
                          <div className="notification-message">{notif.message}</div>
                          <div className="notification-time">
                            {formatNotificationTime(notif.created_at)}
                          </div>
                        </div>
                        {!notif.is_read && <div className="notification-dot"></div>}
                      </div>
                    ))
                  )}
                </div>

                {/* Settings is for super_admin only */}
                {user?.role === 'super_admin' && (
                <div className="notification-footer">
                  <button onClick={() => { setShowNotifications(false); navigate('/settings?tab=notifications'); }}>
                    <Settings aria-hidden="true" /> Notification Settings
                  </button>
                </div>
                )}
              </div>
            )}
          </div>

          <div className="user-menu-container">
            <button 
              className="user-menu-button" 
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt="Profile" />
                ) : (
                  <span>{getInitials(user.first_name, user.last_name)}</span>
                )}
              </div>
              <div className="user-info-header">
                <span className="user-name-header">{user.first_name} {user.last_name}</span>
                <span className="user-role-header">{user.role?.replace('_', ' ')}</span>
              </div>
              <ChevronDown className="dropdown-arrow" aria-hidden="true" />
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="user-avatar-large">
                    {user.profile_picture ? (
                      <img src={user.profile_picture} alt="Profile" />
                    ) : (
                      <span>{getInitials(user.first_name, user.last_name)}</span>
                    )}
                  </div>
                  <h3>{user.first_name} {user.last_name}</h3>
                  <p>{user.email}</p>
                  <span className="status status--neutral">
                    {user.role?.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="dropdown-menu">
                  <button onClick={() => { setShowUserMenu(false); navigate('/profile'); }} className="dropdown-item">
                    <User aria-hidden="true" /> My Profile
                  </button>
                  {user?.role === 'super_admin' && (
                  <button onClick={() => { setShowUserMenu(false); navigate('/settings'); }} className="dropdown-item">
                    <Settings aria-hidden="true" /> Settings
                  </button>
                  )}
                  <button onClick={() => { setShowUserMenu(false); navigate('/security'); }} className="dropdown-item">
                    <Lock aria-hidden="true" /> Security
                  </button>
                  <button onClick={() => { setShowUserMenu(false); navigate('/activity'); }} className="dropdown-item">
                    <Activity aria-hidden="true" /> Activity Log
                  </button>
                  <div className="dropdown-divider"></div>
                  <button onClick={handleLogout} className="dropdown-item logout-item">
                    <LogOut aria-hidden="true" /> Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar with Dropdowns */}
      <div id="app-sidebar" className={`sidebar${navOpen ? ' open' : ''}`}>
        <nav className="sidebar-nav">
          {filteredMenu.map((item, index) => {
            if (item.type === 'dropdown') {
              const isExpanded = expandedMenus[item.label];
              const visibleItems = filterDropdownItems(item.items);
              
              if (visibleItems.length === 0) {
                return null;
              }
              
              return (
                <div key={index} className="nav-dropdown">
                  <button
                    className="nav-item dropdown-toggle"
                    onClick={() => toggleMenu(item.label)}
                  >
                    <item.icon className="nav-icon" aria-hidden="true" />
                    <span className="nav-label">{t(item.label)}</span>
                    {!isExpanded && visibleItems.map((subItem) => badge(subItem.badge)).find(Boolean)}
                    <ChevronDown className={`dropdown-arrow ${isExpanded ? 'expanded' : ''}`} aria-hidden="true" />
                  </button>
                  
                  {isExpanded && (
                    <div className="nav-submenu">
                      {visibleItems.map((subItem, subIndex) => (
                        <button
                          key={subIndex}
                          className={`nav-subitem ${location.pathname === subItem.path ? 'active' : ''}`}
                          onClick={() => go(subItem.path)}
                        >
                          <span className="nav-sublabel">{t(subItem.label)}</span>
                          {badge(subItem.badge)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={index}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => go(item.path)}
              >
                <item.icon className="nav-icon" aria-hidden="true" />
                <span className="nav-label">{t(item.label)}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {children}
      </div>

      {/* Overlays */}
      {navOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setNavOpen(false)}
        ></div>
      )}

      {showUserMenu && (
        <div 
          className="overlay" 
          onClick={() => setShowUserMenu(false)}
        ></div>
      )}
      
      {showNotifications && (
        <div 
          className="overlay" 
          onClick={() => setShowNotifications(false)}
        ></div>
      )}
    </div>
  );
};

export default Layout;